import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Table,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Container,
} from "react-bootstrap";
import { BiSolidReport } from "react-icons/bi";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { FiDownload, FiRefreshCw } from "react-icons/fi";
import { format, startOfWeek, startOfMonth, startOfYear } from "date-fns";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getAssetReturnReport } from "../../services/ReportServices";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import "react-datepicker/dist/react-datepicker.css";

const returnStatusOptions = [
  { value: "", label: "All Status" },
  { value: "PENDING", label: "Pending" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELED", label: "Canceled" },
];

const periodOptions = [
  { label: "All", value: "all" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "7_days" },
  { label: "Last 15 Days", value: "15_days" },
  { label: "Last 30 Days", value: "30_days" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "3_months" },
  { label: "Custom Date", value: "custom" },
];

const AssetReturnReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[4]); // Default to "This Month"

  const [filters, setFilters] = useState({
    asset_number: "",
    return_status: "",
    period: "monthly",
    start_date: "",
    end_date: "",
    page: 1,
    page_size: 10,
  });

  useEffect(() => {
    const today = new Date();
    let start_date = "";
    let end_date = "";

    switch (selectedPeriod?.value) {
      case "all":
        start_date = "";
        end_date = "";
        break;
      case "yesterday":
        start_date = format(
          new Date(today.setDate(today.getDate() - 1)),
          "yyyy-MM-dd"
        );
        end_date = start_date;
        break;
      case "7_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 6)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "15_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 14)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "30_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 29)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "last_month": {
        const firstDayOfCurrentMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );
        const lastDayOfLastMonth = new Date(firstDayOfCurrentMonth - 1);
        const firstDayOfLastMonth = new Date(
          lastDayOfLastMonth.getFullYear(),
          lastDayOfLastMonth.getMonth(),
          1
        );
        start_date = format(firstDayOfLastMonth, "yyyy-MM-dd");
        end_date = format(lastDayOfLastMonth, "yyyy-MM-dd");
        break;
      }
      case "3_months": {
        const firstDayOfCurrentMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );
        const firstDayOf3MonthsAgo = new Date(
          firstDayOfCurrentMonth.setMonth(firstDayOfCurrentMonth.getMonth() - 3)
        );
        start_date = format(firstDayOf3MonthsAgo, "yyyy-MM-dd");
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      }
      case "weekly":
        start_date = format(
          startOfWeek(new Date(), { weekStartsOn: 1 }),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "monthly":
        start_date = format(startOfMonth(new Date()), "yyyy-MM-dd");
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "yearly":
        start_date = format(startOfYear(new Date()), "yyyy-MM-dd");
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "custom":
        // Keep existing custom date from state
        start_date = filters.start_date;
        end_date = filters.end_date;
        break;
      default:
        start_date = "";
        end_date = "";
        break;
    }

    setFilters((prev) => ({
      ...prev,
      period: selectedPeriod?.value || "all",
      start_date,
      end_date,
      page: 1,
    }));
  }, [selectedPeriod]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 1 }));
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      if (
        filters.period === "custom" &&
        (!filters.start_date || !filters.end_date)
      ) {
        setReportData([]);
        setTotalCount(0);
        return;
      }

      const params = {
        ...filters,
        asset_number: filters.asset_number || undefined,
        return_status: filters.return_status || undefined,
        period: filters.period,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        page: filters.page,
        page_size: filters.page_size,
      };

      const response = await getAssetReturnReport(params);
      setReportData(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch report data:", err);
      const message = err.response?.data?.message || "Failed to load report";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const exportParams = {
        ...filters,
        export: true,
      };
      const response = await getAssetReturnReport(exportParams, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "asset_return_report.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Export started successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (selectedPage) => {
    setFilters((prev) => ({ ...prev, page: selectedPage.selected + 1 }));
    window.scrollTo(0, 0);
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "ACCEPTED":
        return "success";
      case "REJECTED":
        return "danger";
      default:
        return "secondary";
    }
  };

  useEffect(() => {
    const delay = setTimeout(fetchReportData, 300);
    return () => clearTimeout(delay);
  }, [
    filters.page,
    filters.page_size,
    filters.asset_number,
    filters.return_status,
    filters.start_date,
    filters.end_date,
  ]);

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="text-primary mb-0">Asset Return Report</h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Asset Return Report</li>
          </ol>
        </div>
        <div
          className="d-flex align-items-center gap-2 ms-auto mt-2 mt-md-0"
          style={{ zIndex: 10 }}
        >
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading}
            style={{ height: "41px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FiDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>

      <Card style={{ minHeight: "350px" }}>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 text-primary fw-bold fs-5 mb-1">
            <BiSolidReport size={22} color="#255d5c" />
            <h5 className="mb-0">Asset Return Report</h5>
          </div>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <DebounceSmallSearchInput
              value={filters.asset_number}
              handleSearchChange={(value) =>
                handleFilterChange("asset_number", value)
              }
              placeholder="Search asset number..."
              debounceTime={500}
            />

            <GlobalSearchableDropdown
              options={returnStatusOptions}
              selected={returnStatusOptions.find(
                (opt) => opt.value === filters.return_status
              )}
              onSelect={(selected) =>
                handleFilterChange("return_status", selected?.value || "")
              }
              placeholder="Return Status"
              width="180px"
            />

            <GlobalSearchableDropdown
              options={periodOptions}
              selected={selectedPeriod}
              onSelect={setSelectedPeriod}
              placeholder="Period"
              width="150px"
            />

            {selectedPeriod?.value === "custom" && (
              <div style={{ width: "260px" }}>
                <DatePicker
                  selectsRange
                  startDate={
                    filters.start_date ? new Date(filters.start_date) : null
                  }
                  endDate={filters.end_date ? new Date(filters.end_date) : null}
                  onChange={([start, end]) => {
                    setFilters((prev) => ({
                      ...prev,
                      start_date: start ? format(start, "yyyy-MM-dd") : "",
                      end_date: end ? format(end, "yyyy-MM-dd") : "",
                      period: "custom",
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
              </div>
            )}
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger">
              {error}
              <Button
                variant="link"
                size="sm"
                onClick={fetchReportData}
                className="p-0 ms-2"
              >
                <FiRefreshCw /> Try again
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading report data...</p>
            </div>
          ) : (
            <Row>
              <Col lg={12}>
                <Card className="h-100">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Asset Number</th>
                            <th>Asset Name</th>
                            <th>Returned By</th>
                            <th>Return Date</th>
                            <th>Reason</th>
                            <th>Location</th>
                            <th>Status</th>
                            <th>Return Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center py-4">
                                No data available
                              </td>
                            </tr>
                          ) : (
                            reportData.map((item, index) => (
                              <tr key={`${item.asset_number}-${index}`}>
                                <td>{item.asset_number || "-"}</td>
                                <td>{item.asset_name || "-"}</td>
                                <td>{item.returned_by || "-"}</td>
                                <td>{item.return_date || "-"}</td>
                                <td>{item.reason || "-"}</td>
                                <td>{item.location || "-"}</td>
                                <td>{item.asset_status || "-"}</td>
                                <td>
                                  <Badge
                                    bg={getStatusBadgeVariant(
                                      item.return_status
                                    )}
                                    className="text-capitalize"
                                  >
                                    {item.return_status || "UNKNOWN"}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                    {totalCount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                        <div>
                          Showing {(filters.page - 1) * filters.page_size + 1}{" "}
                          to{" "}
                          {Math.min(
                            filters.page * filters.page_size,
                            totalCount
                          )}{" "}
                          of {totalCount} entries
                        </div>
                        <Pagination
                          currentPage={filters.page - 0}
                          recordsPage={filters.page_size}
                          dataLength={totalCount}
                          handlePagination={handlePageChange}
                          showPageInfo={false}
                        />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AssetReturnReport;
